/**
 * CL-PRODUCT-SELECTIONS-006A — Product decision status vs record lifecycle (pure).
 *
 * Decision status is user-facing selection progress.
 * Record lifecycle controls working-list visibility (active vs archived).
 * Legacy optionStatus remains synchronized for rules and publication compat.
 */

import {
  PRODUCT_OPTION_STATUS,
  isValidProductOptionStatus,
} from "./projectProductSelectionFoundationModel";

export const PRODUCT_OPTION_RECORD_LIFECYCLE = Object.freeze({
  ACTIVE: "active",
  ARCHIVED: "archived",
});

export const PRODUCT_OPTION_DECISION_STATUS = Object.freeze({
  OPTION: "option",
  SHORT_LIST: "short_list",
  FINAL_SELECTION: "final_selection",
  NOT_SELECTED: "not_selected",
});

export const PRODUCT_OPTION_DECISION_STATUS_LABELS = Object.freeze({
  [PRODUCT_OPTION_DECISION_STATUS.OPTION]: "Option",
  [PRODUCT_OPTION_DECISION_STATUS.SHORT_LIST]: "Short List",
  [PRODUCT_OPTION_DECISION_STATUS.FINAL_SELECTION]: "Final Selection",
  [PRODUCT_OPTION_DECISION_STATUS.NOT_SELECTED]: "Not Selected",
});

export const PRODUCT_OPTION_RECORD_LIFECYCLE_LABELS = Object.freeze({
  [PRODUCT_OPTION_RECORD_LIFECYCLE.ACTIVE]: "Active",
  [PRODUCT_OPTION_RECORD_LIFECYCLE.ARCHIVED]: "Archived",
});

export const ADMIN_PRODUCT_OPTION_DECISION_OPTIONS = Object.freeze([
  { value: PRODUCT_OPTION_DECISION_STATUS.OPTION, label: "Option" },
  { value: PRODUCT_OPTION_DECISION_STATUS.SHORT_LIST, label: "Short List" },
  { value: PRODUCT_OPTION_DECISION_STATUS.FINAL_SELECTION, label: "Final Selection" },
  { value: PRODUCT_OPTION_DECISION_STATUS.NOT_SELECTED, label: "Not Selected" },
]);

const RECORD_LIFECYCLE_VALUES = new Set(Object.values(PRODUCT_OPTION_RECORD_LIFECYCLE));
const DECISION_STATUS_VALUES = new Set(Object.values(PRODUCT_OPTION_DECISION_STATUS));

function safeStr(value) {
  return String(value ?? "").trim();
}

export function isValidProductOptionRecordLifecycle(value) {
  return RECORD_LIFECYCLE_VALUES.has(safeStr(value));
}

export function isValidProductOptionDecisionStatus(value) {
  return DECISION_STATUS_VALUES.has(safeStr(value));
}

export function productOptionDecisionStatusLabel(value = "") {
  return PRODUCT_OPTION_DECISION_STATUS_LABELS[safeStr(value)] || "Option";
}

export function productOptionRecordLifecycleLabel(value = "") {
  return PRODUCT_OPTION_RECORD_LIFECYCLE_LABELS[safeStr(value)] || "Active";
}

/** Map legacy optionStatus to canonical lifecycle + decision fields. */
export function deriveProductOptionLifecycleAndDecision(option = {}, {
  approvedOptionId = "",
} = {}) {
  const legacyStatus = safeStr(option?.optionStatus);
  const storedLifecycle = safeStr(option?.recordLifecycle);
  const storedDecision = safeStr(option?.decisionStatus);
  const optionId = safeStr(option?.id);

  let recordLifecycle = PRODUCT_OPTION_RECORD_LIFECYCLE.ACTIVE;
  if (storedLifecycle && isValidProductOptionRecordLifecycle(storedLifecycle)) {
    recordLifecycle = storedLifecycle;
  } else if (legacyStatus === PRODUCT_OPTION_STATUS.ARCHIVED) {
    recordLifecycle = PRODUCT_OPTION_RECORD_LIFECYCLE.ARCHIVED;
  }

  let decisionStatus = PRODUCT_OPTION_DECISION_STATUS.OPTION;
  if (storedDecision && isValidProductOptionDecisionStatus(storedDecision)) {
    decisionStatus = storedDecision;
  } else if (approvedOptionId && optionId && approvedOptionId === optionId) {
    decisionStatus = PRODUCT_OPTION_DECISION_STATUS.FINAL_SELECTION;
  } else if (option?.isShortlisted === true) {
    decisionStatus = PRODUCT_OPTION_DECISION_STATUS.SHORT_LIST;
  } else if (
    legacyStatus === PRODUCT_OPTION_STATUS.REJECTED
    || legacyStatus === PRODUCT_OPTION_STATUS.SUPERSEDED
  ) {
    decisionStatus = PRODUCT_OPTION_DECISION_STATUS.NOT_SELECTED;
  } else if (
    legacyStatus === PRODUCT_OPTION_STATUS.ACTIVE
    || legacyStatus === PRODUCT_OPTION_STATUS.DRAFT
  ) {
    decisionStatus = PRODUCT_OPTION_DECISION_STATUS.OPTION;
  }

  if (
    recordLifecycle === PRODUCT_OPTION_RECORD_LIFECYCLE.ACTIVE
    && approvedOptionId
    && optionId
    && approvedOptionId === optionId
    && decisionStatus !== PRODUCT_OPTION_DECISION_STATUS.NOT_SELECTED
  ) {
    decisionStatus = PRODUCT_OPTION_DECISION_STATUS.FINAL_SELECTION;
  }

  return { recordLifecycle, decisionStatus };
}

/** Keep legacy optionStatus aligned for rules + publication checks. */
export function syncLegacyOptionStatus(recordLifecycle = "", decisionStatus = "") {
  if (recordLifecycle === PRODUCT_OPTION_RECORD_LIFECYCLE.ARCHIVED) {
    return PRODUCT_OPTION_STATUS.ARCHIVED;
  }
  if (decisionStatus === PRODUCT_OPTION_DECISION_STATUS.NOT_SELECTED) {
    return PRODUCT_OPTION_STATUS.REJECTED;
  }
  return PRODUCT_OPTION_STATUS.ACTIVE;
}

export function normalizeProductOptionDecisionFields(option = {}, {
  approvedOptionId = "",
  preserveArchivedDecision = false,
} = {}) {
  const derived = deriveProductOptionLifecycleAndDecision(option, { approvedOptionId });
  const recordLifecycle = derived.recordLifecycle;
  let decisionStatus = derived.decisionStatus;

  if (
    preserveArchivedDecision
    && recordLifecycle === PRODUCT_OPTION_RECORD_LIFECYCLE.ARCHIVED
    && isValidProductOptionDecisionStatus(option?.decisionStatusBeforeArchive)
  ) {
    decisionStatus = safeStr(option.decisionStatusBeforeArchive);
  }

  const optionStatus = syncLegacyOptionStatus(recordLifecycle, decisionStatus);
  if (!isValidProductOptionStatus(optionStatus)) {
    return { ok: false, error: "Invalid product status." };
  }

  const normalized = {
    recordLifecycle,
    decisionStatus,
    optionStatus,
    isShortlisted: decisionStatus === PRODUCT_OPTION_DECISION_STATUS.SHORT_LIST,
  };

  if (recordLifecycle === PRODUCT_OPTION_RECORD_LIFECYCLE.ARCHIVED) {
    normalized.decisionStatusBeforeArchive = decisionStatus;
  }

  return { ok: true, ...normalized };
}

export function isProductOptionArchived(option = {}, { approvedOptionId = "" } = {}) {
  const { recordLifecycle } = deriveProductOptionLifecycleAndDecision(option, { approvedOptionId });
  return recordLifecycle === PRODUCT_OPTION_RECORD_LIFECYCLE.ARCHIVED;
}

export function isProductOptionFinalSelection(option = {}, { approvedOptionId = "" } = {}) {
  const { decisionStatus } = deriveProductOptionLifecycleAndDecision(option, { approvedOptionId });
  return decisionStatus === PRODUCT_OPTION_DECISION_STATUS.FINAL_SELECTION;
}

export function isProductOptionWorkingListVisible(option = {}, { approvedOptionId = "" } = {}) {
  return !isProductOptionArchived(option, { approvedOptionId });
}

export function buildProductOptionArchivePatch(option = {}, { approvedOptionId = "" } = {}) {
  const derived = deriveProductOptionLifecycleAndDecision(option, { approvedOptionId });
  return {
    recordLifecycle: PRODUCT_OPTION_RECORD_LIFECYCLE.ARCHIVED,
    decisionStatusBeforeArchive: derived.decisionStatus,
    decisionStatus: derived.decisionStatus,
    optionStatus: PRODUCT_OPTION_STATUS.ARCHIVED,
  };
}

export function buildProductOptionRestorePatch(option = {}, { approvedOptionId = "" } = {}) {
  const restoredDecision = isValidProductOptionDecisionStatus(option?.decisionStatusBeforeArchive)
    ? safeStr(option.decisionStatusBeforeArchive)
    : deriveProductOptionLifecycleAndDecision(option, { approvedOptionId }).decisionStatus;

  const recordLifecycle = PRODUCT_OPTION_RECORD_LIFECYCLE.ACTIVE;
  const decisionStatus = restoredDecision;
  return {
    recordLifecycle,
    decisionStatus,
    decisionStatusBeforeArchive: null,
    optionStatus: syncLegacyOptionStatus(recordLifecycle, decisionStatus),
    isShortlisted: decisionStatus === PRODUCT_OPTION_DECISION_STATUS.SHORT_LIST,
  };
}

export function validateProductOptionDecisionChange({
  nextDecisionStatus = "",
  option = {},
  approvedOptionId = "",
} = {}) {
  const next = safeStr(nextDecisionStatus);
  if (!isValidProductOptionDecisionStatus(next)) {
    return { ok: false, error: "Choose a valid decision status." };
  }
  if (isProductOptionArchived(option, { approvedOptionId })) {
    return { ok: false, error: "Restore this product before changing its decision status." };
  }
  return { ok: true, decisionStatus: next };
}

export function buildProductOptionDecisionPatch({
  decisionStatus = "",
  option = {},
  approvedOptionId = "",
} = {}) {
  const validated = validateProductOptionDecisionChange({
    nextDecisionStatus: decisionStatus,
    option,
    approvedOptionId,
  });
  if (!validated.ok) return validated;

  const recordLifecycle = PRODUCT_OPTION_RECORD_LIFECYCLE.ACTIVE;
  return {
    ok: true,
    patch: {
      recordLifecycle,
      decisionStatus: validated.decisionStatus,
      optionStatus: syncLegacyOptionStatus(recordLifecycle, validated.decisionStatus),
      isShortlisted: validated.decisionStatus === PRODUCT_OPTION_DECISION_STATUS.SHORT_LIST,
    },
    clearsApprovedOptionId: validated.decisionStatus !== PRODUCT_OPTION_DECISION_STATUS.FINAL_SELECTION
      && approvedOptionId
      && safeStr(option?.id) === safeStr(approvedOptionId),
    setsApprovedOptionId: validated.decisionStatus === PRODUCT_OPTION_DECISION_STATUS.FINAL_SELECTION,
  };
}

export function resolveFinalSelectionOptionId(options = [], approvedOptionId = "") {
  const approved = safeStr(approvedOptionId);
  if (approved) {
    const match = (Array.isArray(options) ? options : []).find((row) => safeStr(row?.id) === approved);
    if (match && isProductOptionFinalSelection(match, { approvedOptionId: approved })) {
      return approved;
    }
  }
  const fromDecision = (Array.isArray(options) ? options : []).find((row) => (
    deriveProductOptionLifecycleAndDecision(row, { approvedOptionId: approved }).decisionStatus
      === PRODUCT_OPTION_DECISION_STATUS.FINAL_SELECTION
  ));
  return safeStr(fromDecision?.id) || null;
}

export function buildFinalSelectionTransactionPlan({
  optionId = "",
  options = [],
  requirement = {},
} = {}) {
  const targetId = safeStr(optionId);
  if (!targetId) return { ok: false, error: "Choose a product." };

  const approvedOptionId = safeStr(requirement?.approvedOptionId);
  const previousFinalId = resolveFinalSelectionOptionId(options, approvedOptionId);
  const optionUpdates = [];
  const clearsApproved = [];

  (Array.isArray(options) ? options : []).forEach((row) => {
    const id = safeStr(row?.id);
    if (!id) return;
    if (id === targetId) {
      optionUpdates.push({
        id,
        patch: buildProductOptionDecisionPatch({
          decisionStatus: PRODUCT_OPTION_DECISION_STATUS.FINAL_SELECTION,
          option: row,
          approvedOptionId,
        }).patch,
      });
      return;
    }
    const derived = deriveProductOptionLifecycleAndDecision(row, { approvedOptionId });
    if (derived.decisionStatus === PRODUCT_OPTION_DECISION_STATUS.FINAL_SELECTION) {
      const fallbackDecision = row?.isShortlisted === true
        || derived.decisionStatus === PRODUCT_OPTION_DECISION_STATUS.SHORT_LIST
        ? PRODUCT_OPTION_DECISION_STATUS.SHORT_LIST
        : PRODUCT_OPTION_DECISION_STATUS.OPTION;
      optionUpdates.push({
        id,
        patch: buildProductOptionDecisionPatch({
          decisionStatus: fallbackDecision,
          option: row,
          approvedOptionId,
        }).patch,
      });
    }
  });

  return {
    ok: true,
    approvedOptionId: targetId,
    previousFinalOptionId: previousFinalId,
    optionUpdates,
    clearsApproved,
  };
}

export function isProductOptionPublishableByDecision(option = {}, { approvedOptionId = "" } = {}) {
  const derived = deriveProductOptionLifecycleAndDecision(option, { approvedOptionId });
  if (derived.recordLifecycle === PRODUCT_OPTION_RECORD_LIFECYCLE.ARCHIVED) return false;
  if (derived.decisionStatus === PRODUCT_OPTION_DECISION_STATUS.NOT_SELECTED) return false;
  return syncLegacyOptionStatus(derived.recordLifecycle, derived.decisionStatus)
    === PRODUCT_OPTION_STATUS.ACTIVE;
}
